<script setup lang="ts">
import { computed } from 'vue';
import {
    TrashIcon,
    PencilSquareIcon,
    DocumentCheckIcon,
    DocumentArrowDownIcon,
    DocumentTextIcon,
    CheckCircleIcon,
    XMarkIcon,
} from '@heroicons/vue/20/solid';
import type { InvoiceIndexEntry } from '@/packages/api/src';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/packages/ui/src/dropdown-menu';

const props = defineProps<{
    invoice: InvoiceIndexEntry;
}>();

const emit = defineEmits<{
    delete: [];
    edit: [];
    'set-paid': [];
    'set-unpaid': [];
    'download-pdf': [];
    'download-e-invoice': [];
}>();

// Computed property to check if invoice is paid
const isPaid = computed(() => props.invoice.paid_date !== null);
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <button
                class="focus-visible:outline-none focus-visible:bg-card-background rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:opacity-100 hover:bg-card-background group-hover:opacity-100 opacity-20 transition-opacity text-text-secondary"
                :aria-label="'Actions for Invoice ' + props.invoice.reference">
                <svg
                    class="h-8 w-8 p-1 rounded-full"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                        d="M12 5.92A.96.96 0 1 0 12 4a.96.96 0 0 0 0 1.92m0 7.04a.96.96 0 1 0 0-1.92a.96.96 0 0 0 0 1.92M12 20a.96.96 0 1 0 0-1.92a.96.96 0 0 0 0 1.92" />
                </svg>
            </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
            <DropdownMenuItem
                :aria-label="'Edit Invoice ' + props.invoice.reference"
                class="flex items-center space-x-3 cursor-pointer"
                @click="emit('edit')">
                <PencilSquareIcon class="w-5 text-icon-active" />
                <span>Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="!isPaid"
                :aria-label="'Mark Invoice ' + props.invoice.reference + ' as Paid'"
                class="flex items-center space-x-3 cursor-pointer text-success focus:text-success"
                @click="emit('set-paid')">
                <CheckCircleIcon class="w-5" />
                <span>Mark as Paid</span>
            </DropdownMenuItem>
            <DropdownMenuItem
                v-if="isPaid"
                :aria-label="'Mark Invoice ' + props.invoice.reference + ' as Unpaid'"
                class="flex items-center space-x-3 cursor-pointer text-warning focus:text-warning"
                @click="emit('set-unpaid')">
                <XMarkIcon class="w-5" />
                <span>Mark as Unpaid</span>
            </DropdownMenuItem>
            <DropdownMenuItem
                :aria-label="'Delete Invoice ' + props.invoice.reference"
                class="flex items-center space-x-3 cursor-pointer text-destructive focus:text-destructive"
                @click="emit('delete')">
                <TrashIcon class="w-5" />
                <span>Delete</span>
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
